
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CAMERA_CATEGORIES, GE_CATEGORIES } from "@/types/gear";

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  cost_per_unit: number;
  quantity: number;
  department: string | null;
  notes: string | null;
  user_id: string;
}

const CATEGORY_OPTIONS = [...CAMERA_CATEGORIES, ...GE_CATEGORIES];

interface InventoryItemFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  editItem?: any;
}

export function InventoryItemForm({
  open,
  onOpenChange,
  onSuccess,
  editItem
}: InventoryItemFormProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [name, setName] = useState(editItem?.name || "");
  const [category, setCategory] = useState(editItem?.category || CATEGORY_OPTIONS[0]);
  const [quantity, setQuantity] = useState(editItem?.quantity?.toString() || "1");
  const [costPerUnit, setCostPerUnit] = useState(editItem?.cost_per_unit?.toString() || "0.00");
  const [department, setDepartment] = useState(editItem?.department || "camera");
  const [notes, setNotes] = useState(editItem?.notes || "");

  console.log("🔍 FORM DEBUG - Current user when form opens:", user?.id);
  console.log("🔍 FORM DEBUG - Edit item:", editItem);

  const createMutation = useMutation({
    mutationFn: async (formData: any) => {
      if (!user) {
        throw new Error("User must be logged in to add inventory items");
      }

      console.log("🔍 FORM DEBUG - Creating item with data:", {
        ...formData,
        user_id: user.id,
        currentUser: user.id
      });

      // CRITICAL: Explicitly set user_id to prevent cross-contamination
      const itemData = {
        name: formData.name,
        category: formData.category,
        cost_per_unit: formData.cost_per_unit || 0,
        quantity: formData.quantity || 1,
        department: formData.department || "camera",
        notes: formData.notes || null,
        user_id: user.id  // ALWAYS set to current user
      };

      console.log("🔍 FORM DEBUG - Final item data being inserted:", itemData);

      const { data, error } = await supabase
        .from("inventory_items")
        .insert([itemData])
        .select()
        .single();

      if (error) {
        console.error("❌ FORM DEBUG - Error creating item:", error);
        throw error;
      }

      console.log("✅ FORM DEBUG - Item created successfully:", {
        id: data.id,
        name: data.name,
        user_id: data.user_id,
        belongsToCurrentUser: data.user_id === user.id
      });

      return data;
    },
    onSuccess: () => {
      toast.success("Item added successfully!");
      queryClient.invalidateQueries({ queryKey: ["inventory-items", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["category-counts", user?.id] });
      onSuccess?.();
      resetForm();
    },
    onError: (error) => {
      console.error("❌ FORM DEBUG - Mutation error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save item");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (formData: any) => {
      if (!user || !editItem) {
        throw new Error("Missing user or item data");
      }

      console.log("🔍 FORM DEBUG - Updating item:", {
        itemId: editItem.id,
        itemUserId: editItem.user_id,
        currentUser: user.id,
        isOwner: editItem.user_id === user.id
      });

      // SECURITY CHECK: Only allow updating own items
      if (editItem.user_id !== user.id) {
        throw new Error("You can only update your own items");
      }

      const updateData = {
        name: formData.name,
        category: formData.category,
        cost_per_unit: formData.cost_per_unit || 0,
        quantity: formData.quantity || 1,
        department: formData.department || "camera",
        notes: formData.notes || null
      };

      console.log("🔍 FORM DEBUG - Update data:", updateData);

      const { data, error } = await supabase
        .from("inventory_items")
        .update(updateData)
        .eq("id", editItem.id)
        .eq("user_id", user.id)  // Double-check ownership
        .select()
        .single();

      if (error) {
        console.error("❌ FORM DEBUG - Error updating item:", error);
        throw error;
      }

      console.log("✅ FORM DEBUG - Item updated successfully:", data);
      return data;
    },
    onSuccess: () => {
      toast.success("Item updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["inventory-items", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["category-counts", user?.id] });
      onSuccess?.();
      resetForm();
    },
    onError: (error) => {
      console.error("❌ FORM DEBUG - Update error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to update item");
    },
  });

  const resetForm = () => {
    setName("");
    setCategory(CATEGORY_OPTIONS[0]);
    setQuantity("1");
    setCostPerUnit("0.00");
    setDepartment("camera");
    setNotes("");
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("You must be logged in to manage inventory");
      return;
    }

    if (!name.trim()) {
      toast.error("Item name is required");
      return;
    }

    if (!category) {
      toast.error("Category is required");
      return;
    }

    const formData = {
      name: name.trim(),
      category,
      cost_per_unit: parseFloat(costPerUnit) || 0,
      quantity: parseInt(quantity) || 1,
      department,
      notes: notes.trim() || null,
    };

    console.log("🔍 FORM DEBUG - Submitting form:", {
      isEditing: !!editItem,
      formData,
      currentUser: user.id
    });

    if (editItem) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editItem ? "Edit Inventory Item" : "Add New Item"}
          </DialogTitle>
          <DialogDescription>
            {editItem ? "Update the details of your inventory item." : "Add a new item to your inventory with rental rates."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Item Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter item name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select value={category} onValueChange={setCategory} required>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="1"
                min="0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cost">Rate per Day</Label>
              <Input
                id="cost"
                type="number"
                step="0.01"
                value={costPerUnit}
                onChange={(e) => setCostPerUnit(e.target.value)}
                placeholder="0.00"
                min="0"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="department">Department</Label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="camera">Camera</SelectItem>
                <SelectItem value="ge">G&E</SelectItem>
                <SelectItem value="audio">Audio</SelectItem>
                <SelectItem value="grip">Grip</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this item"
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Saving..."
                : editItem
                ? "Update Item"
                : "Add Item"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
